import { useCallback, useId, useRef, useState } from "react";
import { Box, ButtonBase } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { colorPalette } from "../../config/colorPalette";
import List from "../List/List";
import {
  controlSx,
  getStatus,
  inputSx,
  labelSx,
  messageSx,
  textSx,
} from "../_shared/fieldStyles";
import { useDismiss } from "../_shared/useDismiss";

/**
 * PhoneInput
 *
 * A country-code box next to a number field.
 *
 * Props:
 * - label: text above the field
 * - prefix: selected country code (default "+91")
 * - prefixOptions: country codes to choose from, e.g. COUNTRY_CODE_OPTIONS from
 *   utils/phoneUtils. Strings or { label, value }. Without it the box is display-only.
 * - onPrefixChange: (code) => void, called when a code is picked
 * - flag: node for the flag slot (a grey square is shown when omitted)
 * - hideFlag: drop the flag slot altogether
 * - error: true, or a message string shown under the field (red state)
 * - success: green state
 * - disabled
 * - id: input id (generated when omitted)
 * - sx: extra styles for the whole field, merged last
 * - anything else (value, onChange, placeholder, ...) goes to the number input
 */
const PhoneInput = ({
  label,
  prefix = "+91",
  prefixOptions,
  onPrefixChange,
  flag,
  hideFlag = false,
  error,
  success = false,
  disabled = false,
  id,
  sx: sxOverride = {},
  ...rest
}) => {
  const generatedId = useId();
  const inputId = id || generatedId;
  const [open, setOpen] = useState(false);
  const prefixRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, prefixRef);

  const status = getStatus({ error, success, disabled });
  const message = typeof error === "string" ? error : null;
  const filled = Boolean(rest.value || rest.defaultValue);
  const prefixColor = disabled
    ? colorPalette.mercuryAlpha[28]
    : filled
      ? colorPalette.mercury[950]
      : colorPalette.mercuryAlpha[65];
  const canPick = Boolean(prefixOptions?.length) && !disabled;
  const PrefixRoot = canPick ? ButtonBase : Box;
  const Chevron = open ? KeyboardArrowUpIcon : KeyboardArrowDownIcon;
  const items = (prefixOptions || []).map((option) =>
    typeof option === "string" ? { label: option, value: option } : option,
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        ...sxOverride,
      }}
    >
      {label && (
        <Box component="label" htmlFor={inputId} sx={labelSx(disabled)}>
          {label}
        </Box>
      )}
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          // Figma shows both boxes with the focus border while typing
          ...(status === "default" && {
            "&:focus-within [data-control]": {
              borderColor: colorPalette.brand[700],
            },
          }),
        }}
      >
        <Box
          ref={prefixRef}
          sx={{ position: "relative", flexShrink: 0, width: 104 }}
        >
          <PrefixRoot
            data-control
            {...(canPick
              ? {
                  onClick: () => setOpen((current) => !current),
                  "aria-haspopup": "listbox",
                  "aria-expanded": open,
                  "aria-label": "Country code",
                }
              : {})}
            sx={{
              ...controlSx(status, { padding: 7, focusWithin: false }),
              ...textSx,
              width: "100%",
              gap: "8px",
              color: prefixColor,
            }}
          >
            {!hideFlag && (
              <Box
                component="span"
                aria-hidden="true"
                sx={{
                  display: "inline-flex",
                  flexShrink: 0,
                  width: 24,
                  height: 24,
                  borderRadius: "4px",
                  overflow: "hidden",
                  backgroundColor: flag
                    ? "transparent"
                    : colorPalette.mercuryAlpha[5],
                }}
              >
                {flag}
              </Box>
            )}
            <span>{prefix}</span>
            <Chevron
              aria-hidden="true"
              sx={{ fontSize: 20, color: prefixColor }}
            />
          </PrefixRoot>
          {open && (
            <Box
              sx={{
                position: "absolute",
                top: "calc(100% + 4px)",
                left: 0,
                zIndex: 10,
                width: 160,
                maxHeight: 240,
                overflowY: "auto",
                borderRadius: "8px",
              }}
            >
              <List
                items={items}
                value={prefix}
                onChange={(next) => {
                  onPrefixChange?.(next);
                  setOpen(false);
                }}
              />
            </Box>
          )}
        </Box>
        <Box
          data-control
          sx={{
            ...controlSx(status, { focusWithin: false }),
            flex: 1,
            minWidth: 0,
          }}
        >
          <Box
            component="input"
            id={inputId}
            type="tel"
            disabled={disabled}
            aria-invalid={status === "error" || undefined}
            aria-describedby={message ? `${inputId}-error` : undefined}
            {...rest}
            sx={inputSx(disabled)}
          />
        </Box>
      </Box>
      {message && (
        <Box id={`${inputId}-error`} role="alert" sx={messageSx}>
          {message}
        </Box>
      )}
    </Box>
  );
};

export default PhoneInput;
